/**
 * Studio Plaz · Trust Center — institutional, sober, no platform chrome.
 *
 * Routes (handled via simple state machine, not React Router):
 *   /trust                  → landing + request-access form
 *   /trust/access?token=    → token verification + doc index
 *
 * Aesthetic reference: Stripe / Vercel / Linear / OpenAI Enterprise.
 *  No marketing flair, no startup energy, no sidebar.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURPOSE_OPTIONS = [
  { value: "general_inquiry", label: "General inquiry" },
  { value: "responsible_disclosure", label: "Responsible disclosure / security" },
  { value: "enterprise_evaluation", label: "Enterprise evaluation" },
  { value: "investor_due_diligence", label: "Investor due diligence" },
  { value: "partner_integration", label: "Partner integration" },
  { value: "grant_review", label: "Grant evaluator review" },
];

function Frame({ children }) {
  return (
    <div
      data-testid="trust-center-frame"
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        color: "#0b0f17",
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Inter", sans-serif',
        fontSize: 16,
        lineHeight: 1.65,
      }}
    >
      <header
        style={{
          maxWidth: 920,
          margin: "0 auto",
          padding: "32px 28px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <a
          href="/trust"
          data-testid="trust-brand"
          style={{
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "#0b0f17",
            textDecoration: "none",
            fontSize: 15,
          }}
        >
          Studio Plaz · Trust Center
        </a>
        <nav style={{ display: "flex", gap: 18, fontSize: 13, color: "#6b7280" }}>
          <a href="/trust" style={{ color: "#6b7280", textDecoration: "none" }}>Index</a>
          <a
            href="mailto:security@studioplaz.com"
            style={{ color: "#6b7280", textDecoration: "none" }}
            data-testid="trust-security-contact"
          >
            security@studioplaz.com
          </a>
        </nav>
      </header>
      <main style={{ maxWidth: 920, margin: "0 auto", padding: "48px 28px 96px" }}>
        {children}
      </main>
      <footer
        style={{
          maxWidth: 920,
          margin: "0 auto",
          padding: "0 28px 32px",
          color: "#9ca3af",
          fontSize: 12,
          letterSpacing: "0.04em",
        }}
      >
        Studio Plaz · governed institutional knowledge · accessed views are logged
      </footer>
    </div>
  );
}

function Landing() {
  const [form, setForm] = useState({ email: "", organization: "", purpose: "general_inquiry" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/trust/request-access`, form);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Request failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Frame>
      <h1
        data-testid="trust-landing-title"
        style={{ fontSize: 36, fontWeight: 700, letterSpacing: "-0.02em", margin: "0 0 16px" }}
      >
        Studio Plaz Trust Center
      </h1>
      <p style={{ color: "#374151", margin: "0 0 28px", maxWidth: 640 }}>
        A governed disclosure surface for prospects, investors, partners, and grant
        evaluators. Every document is human-curated, audited daily, and tracked at
        access. Canonical internal documents are never exposed.
      </p>

      <section
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          padding: 28,
          marginBottom: 32,
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 6px" }}>
          Request access
        </h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 22px" }}>
          We'll email you a single-use link that expires in 7 days. Access is
          scoped to your stated purpose and is logged.
        </p>

        {result?.ok ? (
          <div
            data-testid="trust-request-success"
            style={{
              padding: 16,
              borderRadius: 10,
              background: "#ecfdf5",
              border: "1px solid #a7f3d0",
              color: "#065f46",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Magic link issued.</div>
            <div style={{ fontSize: 14 }}>{result.message}</div>
            {result.debug_url && (
              <div style={{ marginTop: 14, fontSize: 12, color: "#374151" }}>
                <div style={{ marginBottom: 4 }}>
                  <strong>Dev mode</strong> — email delivery is disabled. Use this link:
                </div>
                <a
                  href={result.debug_url}
                  data-testid="trust-debug-magic-link"
                  style={{ color: "#4f46e5", wordBreak: "break-all" }}
                >
                  {result.debug_url}
                </a>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151" }}>
              Work email
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="trust-request-email"
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontSize: 14,
                  background: "#fafafa",
                }}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151" }}>
              Organization
              <input
                type="text"
                required
                minLength={2}
                value={form.organization}
                onChange={(e) => setForm({ ...form, organization: e.target.value })}
                data-testid="trust-request-org"
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontSize: 14,
                  background: "#fafafa",
                }}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#374151" }}>
              Purpose
              <select
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                data-testid="trust-request-purpose"
                style={{
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontSize: 14,
                  background: "#fafafa",
                }}
              >
                {PURPOSE_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {error && (
              <div
                data-testid="trust-request-error"
                style={{
                  padding: 10,
                  borderRadius: 8,
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  color: "#991b1b",
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="trust-request-submit"
              style={{
                marginTop: 8,
                padding: "10px 18px",
                borderRadius: 8,
                border: "1px solid #4338ca",
                background: submitting ? "#a5b4fc" : "#4f46e5",
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                cursor: submitting ? "default" : "pointer",
                width: "fit-content",
              }}
            >
              {submitting ? "Sending…" : "Request access"}
            </button>
          </form>
        )}
      </section>

      <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 32 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 8px" }}>
            What's exposed
          </h3>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#374151", fontSize: 14 }}>
            <li>Security & privacy posture summaries</li>
            <li>GDPR readiness statement</li>
            <li>High-level architecture overview</li>
            <li>Compliance roadmap & governance principles</li>
            <li>Responsible disclosure policy</li>
          </ul>
        </div>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 8px" }}>
            What's never exposed
          </h3>
          <ul style={{ paddingLeft: 18, margin: 0, color: "#374151", fontSize: 14 }}>
            <li>Internal architecture, schemas, or queue names</li>
            <li>Roadmap specifics or feature sequencing</li>
            <li>Operational playbooks or admin procedures</li>
            <li>Financial internals or pricing levers</li>
            <li>Environment variables, vendor specifics, secrets</li>
          </ul>
        </div>
      </section>
    </Frame>
  );
}

function Access() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const [verifying, setVerifying] = useState(true);
  const [claims, setClaims] = useState(null);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState("");
  const [viewer, setViewer] = useState(null); // { path, title, htmlUrl }

  useEffect(() => {
    if (!token) {
      setError("Missing access token.");
      setVerifying(false);
      return;
    }
    (async () => {
      try {
        const verifyResp = await axios.get(`${API}/trust/verify`, { params: { token } });
        setClaims(verifyResp.data);
        const docsResp = await axios.get(`${API}/trust/docs`, { params: { token } });
        setDocs(docsResp.data?.docs || []);
      } catch (err) {
        setError(err?.response?.data?.detail || err.message || "Verification failed.");
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  if (verifying) {
    return (
      <Frame>
        <p data-testid="trust-verifying">Verifying access…</p>
      </Frame>
    );
  }
  if (error) {
    return (
      <Frame>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 12px" }}>
          Access denied
        </h1>
        <p data-testid="trust-access-error" style={{ color: "#991b1b" }}>{error}</p>
        <p>
          <a href="/trust" style={{ color: "#4f46e5" }}>Request a new link</a>
        </p>
      </Frame>
    );
  }

  if (viewer) {
    return (
      <Frame>
        <button
          onClick={() => setViewer(null)}
          data-testid="trust-back-to-index"
          style={{
            marginBottom: 16,
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid #d1d5db",
            background: "#fff",
            color: "#374151",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          ← Back to documents
        </button>
        <iframe
          title={viewer.title}
          src={viewer.htmlUrl}
          data-testid="trust-doc-iframe"
          style={{
            width: "100%",
            height: "78vh",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            background: "#fff",
          }}
        />
      </Frame>
    );
  }

  return (
    <Frame>
      <h1
        data-testid="trust-access-greeting"
        style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", margin: "0 0 6px" }}
      >
        Welcome, {claims?.organization}
      </h1>
      <p style={{ color: "#6b7280", margin: "0 0 28px", fontSize: 14 }}>
        Purpose: <span style={{ color: "#374151" }}>{(claims?.purpose || "").replace(/_/g, " ")}</span>
        {" · "}Access expires{" "}
        <span style={{ color: "#374151" }}>
          {claims?.expires_at ? new Date(claims.expires_at).toLocaleDateString() : "soon"}
        </span>
        {" · "}Scope:{" "}
        {(claims?.doc_scope || []).map((s) => (
          <span
            key={s}
            style={{
              display: "inline-block",
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 999,
              background: "#eef2ff",
              color: "#3730a3",
              marginRight: 4,
            }}
          >
            {s}
          </span>
        ))}
      </p>

      {docs.length === 0 ? (
        <div
          data-testid="trust-no-docs"
          style={{
            padding: 18,
            border: "1px dashed #d1d5db",
            borderRadius: 12,
            color: "#6b7280",
            fontSize: 14,
          }}
        >
          No documents are currently published for your access scope. The Trust Center
          requires explicit human curation before any document is exposed; check back
          shortly.
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {docs.map((d) => (
            <li
              key={d.path}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                padding: "14px 18px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              data-testid={`trust-doc-row-${d.path}`}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{d.title}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {d.scope}
                  {d.last_reviewed ? ` · last reviewed ${d.last_reviewed}` : ""}
                </div>
              </div>
              <button
                onClick={() =>
                  setViewer({
                    title: d.title,
                    path: d.path,
                    htmlUrl: `${API}/trust/docs/${d.path}?token=${token}`,
                  })
                }
                data-testid={`trust-open-doc-${d.path}`}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  background: "#0b0f17",
                  color: "#fff",
                  border: "1px solid #0b0f17",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Open
              </button>
            </li>
          ))}
        </ul>
      )}
    </Frame>
  );
}

export default function TrustCenter() {
  // Tiny route discriminator — no React Router dependency.
  const path = (typeof window !== "undefined" ? window.location.pathname : "/trust") || "/trust";
  if (path.startsWith("/trust/access")) {
    return <Access />;
  }
  return <Landing />;
}
